/**
 * Canonical recurringConfig shape — daily run mode vs createTimes/createTime.
 * Mirrors server/utils/recurringConfigSanitize.js
 */

import {
  DEFAULT_RECURRING_RESET_TIME,
  DEFAULT_INTERVAL_START_TIME,
  normalizeTimeString,
  sortTimeStrings,
  timeToMinutes,
} from "./recurringDailyTimes";

const VALID_DAILY_RUN_MODES = new Set(["single", "slots", "interval"]);

const VALID_YEARLY_INTERVAL_MONTHS = new Set([2, 3, 4, 5, 6, 8, 9, 10, 11, 12]);

function normalizeYearlyIntervalMonths(value) {
  const n = Number(value);
  if (VALID_YEARLY_INTERVAL_MONTHS.has(n)) return n;
  return 12;
}

/**
 * @param {object|null|undefined} config
 * @param {"load"|"save"} phase
 */
export function sanitizeRecurringConfig(config, phase = "save") {
  if (!config || typeof config !== "object") return config;

  const out = { ...config };
  const cadence = out.cadence || "daily";

  if (cadence === "daily") {
    sanitizeDailyFields(out, phase);
    if (phase === "save") {
      out.dailyEveryMonthsEnabled = false;
    }
  }

  if (cadence === "yearly") {
    out.yearlyIntervalMonths = normalizeYearlyIntervalMonths(out.yearlyIntervalMonths);
  } else if (out.yearlyIntervalMonths == null) {
    out.yearlyIntervalMonths = 12;
  }

  if (phase === "save" && out.dailyEveryDaysEnabled && out.dailyEveryMonthsEnabled) {
    out.dailyEveryMonthsEnabled = false;
  }

  return out;
}

function sanitizeDailyFields(out, _phase) {
  const rawTimes = Array.isArray(out.createTimes)
    ? out.createTimes.map((t) => normalizeTimeString(t)).filter(Boolean)
    : [];

  let mode = VALID_DAILY_RUN_MODES.has(out.dailyRunMode) ? out.dailyRunMode : null;
  if (!mode) {
    mode = rawTimes.length > 1 ? "slots" : "single";
  }
  out.dailyRunMode = mode;

  if (mode === "single") {
    out.createTime = normalizeTimeString(out.createTime || rawTimes[0]);
    delete out.createTimes;
  } else if (mode === "slots") {
    let times = rawTimes.length
      ? sortTimeStrings(rawTimes)
      : [normalizeTimeString(DEFAULT_RECURRING_RESET_TIME)];
    if (!times.length) {
      times = [normalizeTimeString(DEFAULT_RECURRING_RESET_TIME)];
    }
    out.createTimes = times;
    const resetRaw = out.createTime;
    const firstSlot = times[0];
    let resetAt = normalizeTimeString(resetRaw || DEFAULT_RECURRING_RESET_TIME);
    if (
      normalizeTimeString(resetRaw) === normalizeTimeString(firstSlot) &&
      (times.length > 1 || timeToMinutes(resetAt) <= timeToMinutes(firstSlot))
    ) {
      resetAt = normalizeTimeString(DEFAULT_RECURRING_RESET_TIME);
    }
    out.createTime = resetAt;
  } else if (mode === "interval") {
    out.dailyIntervalRunsPerDay = Math.max(
      2,
      Math.min(24, Number(out.dailyIntervalRunsPerDay) || 3)
    );
    out.dailyIntervalHours = Math.max(1, Math.min(23, Number(out.dailyIntervalHours) || 4));
    const startTime = normalizeTimeString(
      out.dailyIntervalStartTime || DEFAULT_INTERVAL_START_TIME
    );
    out.dailyIntervalStartTime = startTime;
    let resetAt = normalizeTimeString(out.createTime || DEFAULT_RECURRING_RESET_TIME);
    if (normalizeTimeString(out.createTime) === startTime) {
      resetAt = normalizeTimeString(DEFAULT_RECURRING_RESET_TIME);
    }
    out.createTime = resetAt;
    delete out.createTimes;
  }
}
